import { ArrowLeft, Share2, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { STRINGS } from '@/strings/strings';

export type HeaderWorld = 'aa' | 'kp' | 'sb';

interface Props {
  title: string;
  subtitle: string;
  world: HeaderWorld;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  onShareClick: () => void;
  /** When true, render only the back / favorite / share controls — no
   *  title or subtitle. Used by Story-Output layouts that move the
   *  title + date inside a story-card (PWA #16 Sub-Phase 16.3+). */
  hideTitle?: boolean;
}

/**
 * Top-of-story header. Back button + title + meta line + favorite +
 * share-icon. The share icon is a stub in 6.2.1; 6.2.4 wires it to
 * the same ShareSheet as the full-width footer CTA. When `hideTitle`
 * is true, the title + subtitle slot is omitted (slim mini-header).
 */
export function StoryHeader({
  title,
  subtitle,
  world,
  isFavorite,
  onToggleFavorite,
  onShareClick,
  hideTitle = false,
}: Props) {
  const navigate = useNavigate();
  const copy = STRINGS.storyOutput.page;

  return (
    <header className="flex items-start justify-between gap-3">
      <div className="flex min-w-0 flex-1 items-start gap-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label={copy.back}
          className="shrink-0 rounded-md p-1 text-dream-navy hover:bg-dream-navy/5"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        {!hideTitle && (
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-dream-navy leading-tight">{title}</h1>
            <p className="mt-0.5 text-xs text-dream-navy/60" data-testid="story-meta">
              {subtitle} · {worldLabel(world)}
            </p>
          </div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          onClick={onToggleFavorite}
          aria-label={isFavorite ? copy.favorite.remove : copy.favorite.add}
          aria-pressed={isFavorite}
          className="rounded-md p-1.5 text-dream-navy/70 hover:bg-dream-navy/5"
        >
          <Star
            className="h-5 w-5"
            fill={isFavorite ? 'currentColor' : 'none'}
            color={isFavorite ? 'var(--color-dream-gold)' : 'currentColor'}
          />
        </button>
        <button
          type="button"
          onClick={onShareClick}
          aria-label={copy.shareIconLabel}
          className="rounded-md p-1.5 text-dream-navy/70 hover:bg-dream-navy/5"
        >
          <Share2 className="h-5 w-5" />
        </button>
      </div>
    </header>
  );
}

function worldLabel(world: HeaderWorld): string {
  const meta = STRINGS.storyOutput.page.meta;
  if (world === 'aa') return meta.worldAa;
  if (world === 'kp') return meta.worldKp;
  return meta.worldSb;
}
